import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Star, Clock, Calendar, Users, MapPin, Mail, DollarSign, CheckCircle, X, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { translations, escapeRooms, unavailableDates } from '../data/mock';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const RoomDetailPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = translations[language];

  const room = escapeRooms.find(r => r.id === roomId);

  const [formData, setFormData] = useState({
    email: '',
    eventType: '',
    players: '',
    location: '',
    date: '',
    time: '',
    promoCode: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const isDateUnavailable = !!formData.date && unavailableDates.includes(formData.date);

  // Backend-validated promo state
  // { valid, discount_type, discount_amount, discount_computed, display_label_en, display_label_fr,
  //   booking_date_limit, expires_at, single_use, error }
  const [promoStatus, setPromoStatus] = useState(null);
  const [promoChecking, setPromoChecking] = useState(false);

  // Debounced call to validate promo (re-runs when code, date, or players change)
  useEffect(() => {
    const code = formData.promoCode.trim();
    if (!code) {
      setPromoStatus(null);
      setPromoChecking(false);
      return;
    }

    setPromoChecking(true);
    const handle = setTimeout(async () => {
      try {
        const numPlayers = parseInt(formData.players);
        const res = await axios.post(`${API}/promo-codes/validate`, {
          code,
          event_date: formData.date || null,
          num_players: Number.isFinite(numPlayers) ? numPlayers : null,
        });
        setPromoStatus(res.data);
      } catch (err) {
        console.error('Promo validation error:', err);
        setPromoStatus({ valid: false, error: 'invalid' });
      } finally {
        setPromoChecking(false);
      }
    }, 400);

    return () => clearTimeout(handle);
  }, [formData.promoCode, formData.date, formData.players]);

  // Compute prices using backend-validated promo
  const { totalPrice, discount, finalPrice } = useMemo(() => {
    const numPlayers = parseInt(formData.players) || 0;
    const basePrice = numPlayers * (room?.price || 20);
    let discountAmount = 0;

    if (promoStatus && promoStatus.valid && numPlayers >= 3 && numPlayers <= 6) {
      if (promoStatus.discount_type === 'perPerson') {
        discountAmount = numPlayers * Number(promoStatus.discount_amount || 0);
      } else if (promoStatus.discount_type === 'flatOrder') {
        discountAmount = Math.min(Number(promoStatus.discount_amount || 0), basePrice);
      }
    }

    return {
      totalPrice: basePrice,
      discount: discountAmount,
      finalPrice: Math.max(0, basePrice - discountAmount),
    };
  }, [formData.players, room, promoStatus]);

  if (!room) {
    return (
      <div className="min-h-screen bg-navy-dark flex items-center justify-center pt-20">
        <div className="text-center">
          <h2 className="heading-2 text-gold mb-4">Room not found</h2>
          <button onClick={() => navigate('/')} className="btn-primary" data-testid="room-not-found-home-btn">
            Go Home
          </button>
        </div>
      </div>
    );
  }

  const promoErrorText = (err, bookingDateLimit) => {
    const fmtDate = (iso) => {
      if (!iso) return '';
      try {
        const d = new Date(iso + 'T00:00:00');
        return d.toLocaleDateString(language === 'en' ? 'en-CA' : 'fr-CA', {
          year: 'numeric', month: 'long', day: 'numeric'
        });
      } catch (_) {
        return iso;
      }
    };
    const limit = fmtDate(bookingDateLimit);
    if (err === 'expired') {
      return language === 'en' ? 'This promo code has expired' : 'Ce code promo est expiré';
    }
    if (err === 'already_used') {
      return language === 'en' ? 'This promo code has already been used' : 'Ce code promo a déjà été utilisé';
    }
    if (err === 'date_out_of_range') {
      return language === 'en'
        ? `With this code, booking must be on or before ${limit}`
        : `Avec ce code, la réservation doit être au plus tard le ${limit}`;
    }
    return language === 'en' ? 'Invalid promo code' : 'Code promo invalide';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.eventType || !formData.players || !formData.location || !formData.date || !formData.time) {
      toast.error(t.booking.error);
      return;
    }

    const numPlayers = parseInt(formData.players);
    if (numPlayers < 3 || numPlayers > 6) {
      toast.error(language === 'en' ? 'Number of players must be between 3 and 6.' : 'Le nombre de joueurs doit être entre 3 et 6.');
      return;
    }

    if (isDateUnavailable) {
      toast.error(language === 'en' ? 'Date unavailable' : 'Date non disponible');
      return;
    }

    // If user entered a promo but it's not valid, stop them
    if (formData.promoCode.trim() && (!promoStatus || !promoStatus.valid)) {
      toast.error(
        promoStatus
          ? promoErrorText(promoStatus.error, promoStatus.booking_date_limit)
          : (language === 'en' ? 'Invalid promo code' : 'Code promo invalide')
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        room_id: room.id,
        room_name: language === 'en' ? room.title : room.titleFr,
        email: formData.email,
        event_type: formData.eventType,
        num_players: numPlayers,
        location: formData.location,
        event_date: formData.date,
        event_time: formData.time,
        payment_method: 'Interac e-Transfer',
        promo_code: formData.promoCode.trim() || null,
      };

      await axios.post(`${API}/bookings`, bookingData);

      setShowConfirmation(true);

      setFormData({
        email: '',
        eventType: '',
        players: '',
        location: '',
        date: '',
        time: '',
        promoCode: ''
      });
      setPromoStatus(null);
    } catch (error) {
      console.error('Booking error:', error);
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string' && detail.startsWith('promo_')) {
        const err = detail.replace('promo_', '');
        toast.error(promoErrorText(err, promoStatus?.booking_date_limit));
      } else if (detail === 'invalid_promo_code') {
        toast.error(language === 'en' ? 'Invalid promo code' : 'Code promo invalide');
      } else {
        toast.error(language === 'en' ? 'Failed to submit booking. Please try again.' : 'Échec de la soumission. Veuillez réessayer.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const renderStars = () => {
    const stars = [];
    const fullStars = Math.floor(room.difficulty);
    const hasHalfStar = room.difficulty % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Star key={i} className="w-6 h-6 fill-gold text-gold" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <div key={i} className="relative w-6 h-6">
            <Star className="w-6 h-6 text-gold/30 absolute" />
            <div className="overflow-hidden w-[50%] absolute">
              <Star className="w-6 h-6 fill-gold text-gold" />
            </div>
          </div>
        );
      } else {
        stars.push(
          <Star key={i} className="w-6 h-6 text-gold/30" />
        );
      }
    }
    return stars;
  };

  return (
    <div className="min-h-screen bg-navy-dark pt-24">
      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-testid="booking-confirmation-modal">
          <div className="relative bg-navy-darker border-2 border-gold rounded-2xl p-8 max-w-lg w-full shadow-2xl animate-fade-in">
            <button
              onClick={() => setShowConfirmation(false)}
              className="absolute top-4 right-4 text-cream/70 hover:text-gold transition-colors"
              data-testid="confirmation-close-btn"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="text-center">
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gold/20 rounded-full flex items-center justify-center border-2 border-gold">
                  <CheckCircle className="w-10 h-10 text-gold" />
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gold mb-6 countdown-font">
                {language === 'en' ? 'Booking Submitted!' : 'Réservation soumise!'}
              </h3>

              <div className="bg-gold/10 border border-gold/50 rounded-xl p-6 mb-6">
                <p className="text-cream text-lg leading-relaxed">
                  {language === 'en'
                    ? 'Your booking is complete once the full payment is received via Interac e-Transfer at the following address:'
                    : 'Votre réservation sera confirmée une fois le paiement complet reçu par virement Interac à l\'adresse suivante :'
                  }
                </p>
                <p className="text-gold text-xl font-bold mt-4">
                  finance@t-minus-escape.com
                </p>
              </div>

              <button onClick={() => setShowConfirmation(false)} className="btn-primary w-full" data-testid="confirmation-got-it-btn">
                {language === 'en' ? 'Got it!' : 'Compris!'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="container mx-auto px-6 py-12 max-w-6xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-cream hover:text-gold transition-colors mb-8 group"
          data-testid="back-to-rooms-btn"
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>{language === 'en' ? 'Back to Rooms' : 'Retour aux Salles'}</span>
        </button>

        {/* Room Details */}
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="relative rounded-2xl overflow-hidden aspect-square">
            <img
              src={room.image}
              alt={language === 'en' ? room.title : room.titleFr}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-darker/80 via-transparent to-transparent"></div>
          </div>

          <div>
            <h1 className="heading-1 text-gold mb-2 countdown-font">
              {language === 'en' ? room.title : room.titleFr}
            </h1>
            <p className="text-xl text-gold/70 mb-6 uppercase tracking-wide">
              {language === 'en' ? room.subtitle : room.subtitleFr}
            </p>

            <div className="bg-gold/10 border-2 border-gold rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-8 h-8 text-gold" />
                <div>
                  <span className="text-4xl font-bold text-gold countdown-font">${room.price}</span>
                  <span className="text-lg text-cream ml-2">{t.rooms.price}</span>
                </div>
              </div>
              <p className="text-sm text-cream/70 mt-3">
                {t.booking.refundPolicy}
              </p>
            </div>

            <p className="body-medium text-cream/90 mb-8 leading-relaxed">
              {language === 'en' ? room.description : room.descriptionFr}
            </p>

            <div className="space-y-4 bg-navy-darker border border-gold/20 rounded-xl p-6">
              <div className="flex items-center gap-3">
                <Star className="w-6 h-6 text-gold" />
                <div>
                  <p className="caption text-cream/70">{t.rooms.difficulty}</p>
                  <div className="flex gap-1 mt-1">{renderStars()}</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="w-6 h-6 text-gold" />
                <div>
                  <p className="caption text-cream/70">{t.rooms.record}</p>
                  <p className="body-small text-cream font-semibold">
                    {language === 'en' ? room.recordTime : room.recordTimeFr}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Booking Form */}
        <div className="bg-navy-darker border border-gold/20 rounded-2xl p-12 max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="heading-2 text-gold mb-4">{t.booking.title}</h2>
            <p className="body-medium text-cream/80">{t.booking.subtitle}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block body-small text-cream mb-2">
                <Mail className="inline w-4 h-4 mr-2" />
                {t.booking.email}
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-navy-dark border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                placeholder={language === 'en' ? 'your@email.com' : 'votre@email.com'}
                disabled={isSubmitting}
                data-testid="booking-email-input"
              />
            </div>

            <div>
              <label className="block body-small text-cream mb-2">
                <Calendar className="inline w-4 h-4 mr-2" />
                {t.booking.eventType}
              </label>
              <select
                name="eventType"
                value={formData.eventType}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-navy-dark border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                disabled={isSubmitting}
                data-testid="booking-event-type-select"
              >
                <option value="">{language === 'en' ? 'Select event type' : 'Sélectionnez le type d\'événement'}</option>
                <option value="private">{t.booking.privateParty}</option>
                <option value="school">{t.booking.schoolEvent}</option>
                <option value="office">{t.booking.officeEvent}</option>
              </select>
            </div>

            <div>
              <label className="block body-small text-cream mb-2">
                <Users className="inline w-4 h-4 mr-2" />
                {t.booking.players} (3-6)
              </label>
              <input
                type="number"
                name="players"
                value={formData.players}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-navy-dark border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                placeholder="3-6"
                min="3"
                max="6"
                disabled={isSubmitting}
                data-testid="booking-players-input"
              />
              {formData.players && (parseInt(formData.players) < 3 || parseInt(formData.players) > 6) && (
                <p className="text-red-400 text-sm mt-2">
                  {language === 'en' ? 'Please enter between 3 and 6 players' : 'Veuillez entrer entre 3 et 6 joueurs'}
                </p>
              )}
            </div>

            <div>
              <label className="block body-small text-cream mb-2">
                <MapPin className="inline w-4 h-4 mr-2" />
                {t.booking.location}
              </label>
              <input
                type="text"
                name="location"
                value={formData.location}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-navy-dark border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                placeholder={language === 'en' ? '123 Main St, City' : '123 rue Principale, Ville'}
                disabled={isSubmitting}
                data-testid="booking-location-input"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block body-small text-cream mb-2">
                  <Calendar className="inline w-4 h-4 mr-2" />
                  {t.booking.date}
                </label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-navy-dark border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                  disabled={isSubmitting}
                  data-testid="booking-date-input"
                />
                {isDateUnavailable && (
                  <p className="text-red-400 text-sm mt-2" data-testid="date-unavailable-msg">
                    {language === 'en' ? 'Date unavailable' : 'Date non disponible'}
                  </p>
                )}
              </div>

              <div>
                <label className="block body-small text-cream mb-2">
                  <Clock className="inline w-4 h-4 mr-2" />
                  {t.booking.time}
                </label>
                <input
                  type="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-navy-dark border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                  disabled={isSubmitting}
                  data-testid="booking-time-input"
                />
              </div>
            </div>

            {/* Promo Code */}
            <div>
              <label className="block body-small text-cream mb-2">
                <Tag className="inline w-4 h-4 mr-2" />
                {language === 'en' ? 'Promo Code (optional)' : 'Code promo (optionnel)'}
              </label>
              <input
                type="text"
                name="promoCode"
                value={formData.promoCode}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-navy-dark border border-gold/30 rounded-lg text-cream focus:outline-none focus:border-gold transition-colors"
                placeholder={language === 'en' ? 'Enter promo code' : 'Entrez le code promo'}
                disabled={isSubmitting}
                data-testid="booking-promo-input"
              />
              {formData.promoCode.trim() && promoChecking && (
                <p className="text-cream/60 text-sm mt-2" data-testid="promo-checking-msg">
                  {language === 'en' ? 'Checking code…' : 'Vérification du code…'}
                </p>
              )}
              {formData.promoCode.trim() && !promoChecking && promoStatus?.valid && (
                <p className="text-green-400 text-sm mt-2" data-testid="promo-valid-msg">
                  ✓ {language === 'en'
                    ? `Promo applied: ${promoStatus.display_label_en || 'discount applied'}`
                    : `Promo appliquée: ${promoStatus.display_label_fr || 'rabais appliqué'}`}
                </p>
              )}
              {formData.promoCode.trim() && !promoChecking && promoStatus && !promoStatus.valid && (
                <p className="text-red-400 text-sm mt-2" data-testid="promo-invalid-msg">
                  {promoErrorText(promoStatus.error, promoStatus.booking_date_limit)}
                </p>
              )}
            </div>

            {/* Total Price Display */}
            {formData.players && parseInt(formData.players) >= 3 && parseInt(formData.players) <= 6 && (
              <div className="bg-gold/10 border-2 border-gold rounded-lg p-6" data-testid="booking-total-price">
                <div className="flex justify-between items-center">
                  <span className="body-medium text-cream">{t.booking.totalPrice}:</span>
                  <div className="text-right">
                    {discount > 0 && (
                      <span className="text-lg text-cream/50 line-through mr-3">
                        ${totalPrice}
                      </span>
                    )}
                    <span className="text-3xl font-bold text-gold countdown-font">
                      ${finalPrice}
                    </span>
                  </div>
                </div>
                <p className="text-sm text-cream/70 mt-3 text-center">
                  {formData.players} {language === 'en' ? 'players' : 'joueurs'} × ${room.price} = ${totalPrice}
                  {discount > 0 && (
                    <span className="text-green-400 block mt-1">
                      {language === 'en' ? `Discount: -$${discount}` : `Rabais: -${discount}$`}
                    </span>
                  )}
                </p>
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={isSubmitting || isDateUnavailable}
              data-testid="booking-submit-btn"
            >
              {isSubmitting ? (language === 'en' ? 'Submitting...' : 'Envoi...') : t.booking.submit}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
